import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Customer } from '@frontend/models';

@Injectable({ providedIn: 'root' })
export class CustomerFormService {

  constructor(private readonly _fb: FormBuilder) { }

  createBusinessCustomerFormGroup(): FormGroup {
    return this._fb.group({
      id: undefined,
      companyNumber: ['', [Validators.required]],
      companyRegister: ['', [Validators.required]],
      uidNumber: [''],
      contact: this._fb.group({
        address: this._fb.group({
          country: [''],
          city: ['', [Validators.required]],
          zipCode: ['', [Validators.required]],
          streetName: ['', [Validators.required]],
          buildingNumber: ['', [Validators.required]],
          doorNumber: [''],
          stairway: [''],
        }),
        email: ['', [Validators.email, Validators.required]],
        phone: '',
      }),
    });
  }

  createCustomerFormGroup(addQuestionnaire = false, countrySettings?: string): FormGroup {
    const group: FormGroup = this._fb.group({
      id: undefined,
      birthdate: [undefined, [Validators.required]],
      title: '',
      firstname: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      nationality: ['', [Validators.required]],
      contact: this._fb.group({
        address: this._fb.group({
          country: [''],
          city: ['', [Validators.required]],
          zipCode: ['', [Validators.required]],
          streetName: ['', [Validators.required]],
          buildingNumber: ['', [Validators.required]],
          doorNumber: [''],
          stairway: [''],
        }),
        email: ['', [Validators.email, Validators.required]],
        phone: ['', [Validators.required, Validators.pattern(/^\+[0-9]{1,3}[0-9]{4,14}$/)]],
      }),
      pep: this.createPepFormGroup(),
      moneyLaundering: this.createAlmInformationFormGroup(),
    });

    if (addQuestionnaire) {
      const questionnaireGroup = this._fb.group({
        lastLandlord: '',
        moveInAmount: [undefined, this.getValidator('moveInAmount', countrySettings)],
        movingReason: ['', this.getValidator('movingReason', countrySettings)],
        animals: [undefined, this.getValidator('animals', countrySettings)],
        animalType: '',
        musician: [undefined, this.getValidator('musician', countrySettings)],
        musicType: '',
        smoker: [undefined, this.getValidator('smoker', countrySettings)],
        commercialUsage: [undefined, this.getValidator('commercialUsage', countrySettings)],
        primaryResidence: [undefined, this.getValidator('primaryResidence', countrySettings)],
        residentialCommunity: [undefined, this.getValidator('residentialCommunity', countrySettings)],
        canAffordRentAndDeposit: [undefined, this.getValidator('canAffordRentAndDeposit', countrySettings)],
        acceptCreditCheck: [undefined, this.getValidator('acceptCreditCheck', countrySettings)],
        hasBankruptcyProceedings: [undefined, this.getValidator('hasBankruptcyProceedings', countrySettings)],
        hasLawsuit: [undefined, this.getValidator('hasLawsuit', countrySettings)],
        noMisstatements: [undefined, this.getValidator('noMisstatements', countrySettings)],
        hasEnergyPass: [undefined],
        insurance: [undefined, this.getValidator('insurance', countrySettings)],
      });

      group.addControl('questionnaire', questionnaireGroup);

      const customerDetailsGroup = this._fb.group({
        maritalStatus: ['', this.getValidator('maritalStatus', countrySettings)],
        hasChildren: [undefined, this.getValidator('hasChildren', countrySettings)],
        childrenAmountAge: '',
        employer: ['', this.getValidator('employer', countrySettings)],
        employerAddress: '',
        employedSince: Date,
        monthlyIncome: [undefined, this.getValidator('monthlyIncome', countrySettings)],
        socialIncome: [undefined, this.getValidator('socialIncome', countrySettings)],
        socialIncomeAmount: [undefined],
        liabilities: [undefined],
      })

      group.addControl('customerDetails', customerDetailsGroup);

      const dataPrivacyGroup = this._fb.group({
        hasAccepted: false,
        admonitionRightOfWithdrawal: false,
        expenseAllowance: false,
      });

      group.addControl('dataPrivacy', dataPrivacyGroup);

    }

    return group;
  }

  createPepFormGroup(): FormGroup {
    return this._fb.group({
      isPep: undefined,
      wasPepLastYear: undefined,
      isFamilyMemberPep: undefined,
      isKnownToBeCloseToPep: undefined,
      firstName: '',
      lastName: '',
    })
  }

  createAlmInformationFormGroup(): FormGroup {
    return this._fb.group({
      residentPerson: undefined,
      investmentIncome: undefined,
      estateSales: undefined,
      endowment: undefined,
      inheritance: undefined,
      savings: undefined,
      employment: undefined,
      investments: undefined,
      pension: undefined,
      loan: undefined,
      other: undefined,
      otherDetails: '',
    })
  }


  convertToFormGroup(customer: Customer, addQuestionnaire = false, countrySettings = 'AT'): FormGroup {
    const form = this.createCustomerFormGroup(addQuestionnaire, countrySettings);

    if (customer === undefined) {
      return form;
    }

    form.patchValue(customer);

    return form;
  }

  addCustomerValidators(customerGroup: FormGroup): void {
    const firstnameControl = customerGroup.get('firstname');
    const lastnameControl = customerGroup.get('lastname');
    const emailControl = customerGroup.get('contact.email');

    if (firstnameControl) {
      firstnameControl.setValidators([Validators.required]);
      firstnameControl.updateValueAndValidity();
    }
    if (lastnameControl) {
      lastnameControl.setValidators([Validators.required]);
      lastnameControl.updateValueAndValidity();
    }
    if (emailControl) {
      emailControl.setValidators([Validators.email, Validators.required]);
      emailControl.updateValueAndValidity();
    }

    customerGroup.markAllAsTouched();
    customerGroup.markAsDirty();
  }

  private getValidator(fieldName: string, countryCode?: string) {

    if (!countryCode) {
      countryCode = 'AT'; // Default to Austria if no country code is provided
    }

    // Define a mapping for required fields based on country codes
    const countrySpecificValidators: Record<string, string[]> = {
      AT: [
        'moveInAmount',
        'movingReason',
        'animals',
        'musician',
        'smoker',
        'commercialUsage',
        'primaryResidence',
        'residentialCommunity',
        'canAffordRentAndDeposit',
        'acceptCreditCheck',
        'hasBankruptcyProceedings',
        'hasLawsuit',
        'noMisstatements',
        'maritalStatus',
        'hasChildren',
        'employer',
        'monthlyIncome',
      ],
      DE: [
        'maritalStatus',
        'hasChildren',
        'employer',
        'monthlyIncome',
        'socialIncome',
        'moveInAmount',
        'movingReason',
        'animals',
        'acceptCreditCheck',
        'insurance',
      ],
    };

    // Get the list of required fields for the given country code
    const requiredFields = countryCode ? countrySpecificValidators[countryCode] ?? [] : [];

    // Check if the field is required
    if (requiredFields.includes(fieldName)) {
      return [Validators.required];
    }

    return []; // Return no validators if the field is not required
  }
}
